/**
 * Property Management Cache Service
 *
 * Caching for property groups data
 * Uses centralized IndexedDB storage utilities for consistency and performance
 * Handles offline-first approach with automatic sync on mutations
 */

import {
  getStorageItem,
  getStorageItemWithMeta,
  setStorageItem,
  removeStorageItem,
  getAllItems,
  clearStore,
  cleanupExpiredEntries as cleanupExpiredEntriesInStore,
  isIndexedDBAvailable as checkIndexedDBAvailable,
  STORAGE_CONFIG,
  getStore,
} from "@/utils/storage/indexed-db-storage";
import type { GetGroupsResponse, ObjectCode } from "./property-management.types";

// ============================================
// Configuration
// ============================================

export const PROPERTY_CACHE_CONFIG = {
  // Cache duration: 24 hours (data doesn't change frequently)
  CACHE_DURATION_MS: STORAGE_CONFIG.PROPERTY_GROUPS_TTL_MS,
} as const;

// ============================================
// Types
// ============================================

export interface CachedPropertyGroupsData {
  cacheKey: string; // cin:objectCode
  cin: string;
  objectCode: ObjectCode;
  data: GetGroupsResponse;
  timestamp: number;
  expiresAt: number;
  version: number; // For tracking updates
}

export interface CacheStatus {
  exists: boolean;
  isExpired: boolean;
  timestamp?: number;
  expiresAt?: number;
  version?: number;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Generate cache key from cin and objectCode
 */
const generateCacheKey = (cin: string, objectCode: ObjectCode): string => {
  return `${cin}:${objectCode}`;
};

// ============================================
// Cache Operations
// ============================================

/**
 * Get cached property groups data
 * Returns null if cache doesn't exist or is expired
 */
export const getCachedPropertyGroups = async (
  cin: string,
  objectCode: ObjectCode
): Promise<GetGroupsResponse | null> => {
  try {
    const cacheKey = generateCacheKey(cin, objectCode);
    const result = await getStorageItem<CachedPropertyGroupsData>("propertyGroups", cacheKey);

    if (!result) {
      return null;
    }

    return result.data;
  } catch (error) {
    console.error("[PropertyCache] Error accessing cache:", error);
    return null;
  }
};

/**
 * Store property groups data in cache
 */
export const setCachedPropertyGroups = async (
  cin: string,
  objectCode: ObjectCode,
  data: GetGroupsResponse
): Promise<void> => {
  try {
    const cacheKey = generateCacheKey(cin, objectCode);
    const now = Date.now();

    // Get existing entry to increment version
    const existingVersion = await getExistingVersion(cacheKey);

    const cacheEntry: CachedPropertyGroupsData = {
      cacheKey,
      cin,
      objectCode,
      data,
      timestamp: now,
      expiresAt: now + PROPERTY_CACHE_CONFIG.CACHE_DURATION_MS,
      version: existingVersion + 1,
    };

    await setStorageItem(
      "propertyGroups",
      cacheKey,
      cacheEntry,
      PROPERTY_CACHE_CONFIG.CACHE_DURATION_MS
    );
    console.log(
      `[PropertyCache] Cached property groups for ${objectCode} (v${cacheEntry.version})`
    );
  } catch (error) {
    console.error("[PropertyCache] Error storing cache:", error);
  }
};

/**
 * Get existing version number for a cache entry
 */
const getExistingVersion = async (cacheKey: string): Promise<number> => {
  try {
    const result = await getStorageItemWithMeta<CachedPropertyGroupsData>("propertyGroups", cacheKey);
    return result?.version ?? 0;
  } catch {
    return 0;
  }
};

/**
 * Delete cached property groups data
 */
export const deleteCachedPropertyGroups = async (
  cin: string,
  objectCode: ObjectCode
): Promise<void> => {
  try {
    const cacheKey = generateCacheKey(cin, objectCode);
    await removeStorageItem("propertyGroups", cacheKey);
    console.log(`[PropertyCache] Deleted cache for ${objectCode}`);
  } catch (error) {
    console.error("[PropertyCache] Error deleting cache:", error);
  }
};

/**
 * Invalidate cache for a specific objectCode (all CINs)
 * Used after mutations to ensure fresh data is fetched
 */
export const invalidateCacheByObjectCode = async (
  objectCode: ObjectCode
): Promise<void> => {
  try {
    const allItems = await getAllItems<CachedPropertyGroupsData>("propertyGroups");
    const keysToDelete: string[] = [];

    Object.entries(allItems).forEach(([key, entry]) => {
      if (entry.objectCode === objectCode) {
        keysToDelete.push(key);
      }
    });

    await Promise.all(keysToDelete.map((key) => removeStorageItem("propertyGroups", key)));
    console.log(`[PropertyCache] Invalidated all cache for ${objectCode}`);
  } catch (error) {
    console.error("[PropertyCache] Error invalidating cache:", error);
  }
};

/**
 * Get cache status for a specific cin and objectCode
 */
export const getCacheStatus = async (
  cin: string,
  objectCode: ObjectCode
): Promise<CacheStatus> => {
  try {
    const cacheKey = generateCacheKey(cin, objectCode);
    const result = await getStorageItemWithMeta<CachedPropertyGroupsData>("propertyGroups", cacheKey);

    if (!result) {
      return { exists: false, isExpired: false };
    }

    const now = Date.now();
    const expired = result.expiresAt ? now > result.expiresAt : false;
    return {
      exists: true,
      isExpired: expired,
      timestamp: result.timestamp,
      expiresAt: result.expiresAt,
      version: result.version,
    };
  } catch {
    return { exists: false, isExpired: false };
  }
};

/**
 * Check if cache is valid (exists and not expired)
 */
export const isCacheValid = async (
  cin: string,
  objectCode: ObjectCode
): Promise<boolean> => {
  const status = await getCacheStatus(cin, objectCode);
  return status.exists && !status.isExpired;
};

/**
 * Clean up all expired entries
 */
export const cleanupExpiredEntries = async (): Promise<number> => {
  return cleanupExpiredEntriesInStore("propertyGroups");
};

/**
 * Clear all cached property groups data
 */
export const clearAllPropertyCache = async (): Promise<void> => {
  try {
    await clearStore("propertyGroups");
    console.log("[PropertyCache] All cache cleared");
  } catch (error) {
    console.error("[PropertyCache] Error clearing cache:", error);
  }
};

/**
 * Get all cached entries (for debugging)
 */
export const getAllCachedEntries = async (): Promise<CachedPropertyGroupsData[]> => {
  try {
    const allItems = await getAllItems<CachedPropertyGroupsData>("propertyGroups");
    return Object.values(allItems);
  } catch (error) {
    console.error("[PropertyCache] Error getting all entries:", error);
    return [];
  }
};

/**
 * Check if IndexedDB is available
 */
export const isIndexedDBAvailable = (): boolean => {
  return checkIndexedDBAvailable();
};

/**
 * Close database connection and clear store
 * For logout cleanup
 */
export const closeDatabase = (): void => {
  try {
    clearStore("propertyGroups").catch(console.error);
    console.log("[PropertyCache] Database store cleared");
  } catch (error) {
    console.error("[PropertyCache] Error closing database:", error);
  }
};

/**
 * Drop the entire database (for complete cleanup)
 */
export const dropDatabase = async (): Promise<void> => {
  try {
    await clearStore("propertyGroups");
    console.log("[PropertyCache] Database dropped");
  } catch (error) {
    console.error("[PropertyCache] Error dropping database:", error);
  }
};

// Export store for advanced usage
export const store = getStore("propertyGroups");

// Export for testing
export const __test__ = {
  generateCacheKey,
};
